import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MyDoctors } from "@/components/MyDoctors";
import { MyMedications } from "@/components/MyMedications";
import { Loader2, MapPin, Search, Plus, Check, ArrowRight, ShieldCheck, CheckCircle2, X, Mail, HeartHandshake } from "lucide-react";

// Guided-flow chronic conditions → backend csnp_conditions keys. I-SNP is never shown to consumers.
const CONDITIONS = [
  { key: "diabetes", label: "Diabetes" },
  { key: "heart", label: "Heart or circulatory" },
  { key: "lung", label: "COPD or chronic lung" },
];

// Reusable, deterministic 2027 plan comparison flow. Drives the SAME shared compare engine for both the
// anonymous public Ask and the Helm agent Ask (the signed-in household Ask has its own inline compare).
// Public uses the public/ask guest endpoints; agent uses /agent plan-search + compare endpoints. No LLM.
const isSbaJourney = () => {
  try { return new URLSearchParams(window.location.search).get("src") === "sba" || sessionStorage.getItem("ask_src") === "sba"; } catch { return false; }
};

function ReviewCard({ year, onRequest }) {
  const who = isSbaJourney() ? "A licensed SBA advisor" : "A licensed insurance advisor";
  return (
    <div className="mt-3 rounded-xl border-2 border-[#D4AF37] bg-[#FFFBEB] p-4" data-testid="compare-review-card">
      <p className="text-sm font-bold text-[#0B192C]">Before you choose, check your doctors and prescriptions</p>
      <p className="mt-1 text-xs text-[#374151]">For {year}, don’t assume your doctors and prescriptions are covered the same way as this year. Nauticus has not verified provider participation or prescription coverage for the plans shown.</p>
      <p className="mt-1 text-xs text-[#374151]" data-testid="compare-review-card-who">{who} can review your doctors, medications and preferred pharmacy using carrier tools, help confirm details with the plan or provider when needed, and explain any restrictions before you decide.</p>
      <Button type="button" onClick={onRequest} className="mt-3 w-full sm:w-auto h-auto min-h-11 whitespace-normal bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full font-semibold" data-testid="compare-review-card-btn">
        Have an advisor review my doctors and prescriptions
      </Button>
    </div>
  );
}

export function PlanComparePanel({ variant, token, onSaveInvite, onShare, onRequestAdvisor, defaultCounty = "", defaultState = "FL", initialCandidates = [], initialCurrent = null, prefillCarrier = "", eligibilityPrefill = null, onSelfEnroll = null, onSaveCompareAdvisor = null }) {
  const isAgent = variant === "agent";
  const isMember = variant === "member";
  const isConsumer = !isAgent; // member + public get the eligibility-first guided flow
  // Guided eligibility-first flow (consumer only). flow: "" (choose) | "guided" | "known".
  // eligibilityPrefill forces the guided eligibility path (prefilled from the member's known answers).
  const [flow, setFlow] = useState(eligibilityPrefill ? "guided" : (((initialCandidates || []).length > 0 || initialCurrent || prefillCarrier) ? "known" : (variant !== "agent" ? "guided" : "")));
  const [medicaid, setMedicaid] = useState(eligibilityPrefill?.medicaid || "");          // "" | "yes" | "no" | "not_sure"
  const [continueNotSure, setContinueNotSure] = useState(!!(eligibilityPrefill?.medicaid === "not_sure"));
  const [conditions, setConditions] = useState(eligibilityPrefill?.conditions || []);
  const [loadingElig, setLoadingElig] = useState(false);
  const [expandedGeneral, setExpandedGeneral] = useState(false);
  const [zip, setZip] = useState("");
  const [state, setState] = useState(defaultState || "FL");
  const [county, setCounty] = useState(defaultCounty || "");
  const [countyOptions, setCountyOptions] = useState([]);
  const [resolvingZip, setResolvingZip] = useState(false);
  const [carrier, setCarrier] = useState(prefillCarrier || "");
  const [searching, setSearching] = useState(false);
  const [cards, setCards] = useState([]);
  const [searched, setSearched] = useState(false);
  const [candidates, setCandidates] = useState(initialCandidates || []); // selected cards
  const [current, setCurrent] = useState(initialCurrent || null);      // chosen current plan card
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  // Member-only: the current plan is identified against the CURRENT plan year (2026), while
  // candidates are 2027. A dedicated 2026 lookup keeps the two years cleanly separated.
  const [curCarrier, setCurCarrier] = useState("");
  const [curSearching, setCurSearching] = useState(false);
  const [curCards, setCurCards] = useState([]);
  const [curSearched, setCurSearched] = useState(false);

  const keyOf = (c) => c.display_id || `${c.contract_id}-${c.pbp}-${c.segment_id}`;

  const resolveZip = async () => {
    const z = zip.trim();
    if (!z) return;
    setResolvingZip(true); setCountyOptions([]);
    try {
      const { data } = await api.post("/location/resolve-zip", { zip_code: z, state });
      if (data.status === "single") { setCounty(data.county); setState(data.state || state); toast.success(`Found ${data.county} County`); }
      else if (data.status === "multiple") { setCountyOptions(data.counties || []); setState(data.state || state); }
      else { toast.info(data.message || "We don't have that ZIP on file — type your county below."); }
    } catch { toast.error("Couldn't look up that ZIP. You can type your county below."); }
    finally { setResolvingZip(false); }
  };

  const search = async () => {
    if (!carrier.trim() || !county.trim()) { toast.error("Enter a plan carrier and county first."); return; }
    setSearching(true); setSearched(false);
    try {
      let out = [];
      if (isAgent) {
        const { data } = await api.get("/agent/plan-search", { params: { plan_year: 2027, org: carrier, county, state } });
        out = (data.results || []).map((c) => ({
          contract_id: c.contract_id, pbp: c.pbp, segment_id: c.segment_id, display_id: c.display_id,
          plan_name: c.plan_name, organization: c.organization_marketing_name, plan_type: c.plan_type_label,
          snp_type: (c.snp_type && c.snp_type !== "Not Available") ? c.snp_type : null,
        }));
      } else if (isMember) {
        const { data } = await api.get("/consumer/ask/plans/search", { params: { carrier, county, plan_year: 2027 } });
        out = data.plans || [];
      } else {
        const { data } = await api.post(`/public/ask/${token}/plan-search`, { carrier, county, state });
        out = data.cards || [];
      }
      setCards(out); setSearched(true);
      if (out.length === 0) toast.info("No plans on file for that carrier in that county.");
    } catch { toast.error("Plan search failed. Please try again."); }
    finally { setSearching(false); }
  };

  const searchCurrent = async () => {
    if (!curCarrier.trim() || !county.trim()) { toast.error("Enter your current plan's carrier and county first."); return; }
    setCurSearching(true); setCurSearched(false);
    try {
      const { data } = await api.get("/consumer/ask/plans/search", { params: { carrier: curCarrier, county, plan_year: 2026 } });
      setCurCards(data.plans || []); setCurSearched(true);
      if ((data.plans || []).length === 0) toast.info("No 2026 plans on file for that carrier in that county.");
    } catch { toast.error("Current-plan search failed. Please try again."); }
    finally { setCurSearching(false); }
  };

  // Guided eligibility-first flow -------------------------------------------------------------
  const chooseMedicaid = (v) => {
    setMedicaid(v); setContinueNotSure(false); setConditions([]); setCards([]); setSearched(false); setExpandedGeneral(false);
    if (v === "yes") loadEligible("yes", []);   // D-SNP only
  };
  const toggleCondition = (k) =>
    setConditions((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);

  const persistEligibility = async (med, conds) => {
    try {
      if (isMember) await api.post(`/consumer/ask/${token}/eligibility`, { medicaid: med, conditions: conds, county, state });
      else if (isConsumer) await api.post(`/public/ask/${token}/eligibility`, { medicaid: med, conditions: conds, county, state });
    } catch { /* persistence is non-blocking */ }
  };

  const loadEligible = async (med, conds, includeGeneral = false) => {
    if (!county.trim()) { toast.error("Enter your county first."); return; }
    setLoadingElig(true); setSearched(false);
    try {
      const { data } = await api.get("/public/plans/eligible", { params: {
        county, state, medicaid: med === "yes" ? "yes" : "", conditions: (conds || []).join(","),
        include_general: includeGeneral, plan_year: 2027 } });
      setExpandedGeneral(includeGeneral);
      const out = (data.results || []).map((c) => ({
        contract_id: c.contract_id, pbp: c.pbp, segment_id: c.segment_id, display_id: c.display_id,
        plan_name: c.plan_name, organization: c.organization_marketing_name, plan_type: c.plan_type_label,
        snp_type: (c.snp_type && c.snp_type !== "Not Available") ? c.snp_type : null,
      }));
      setCards(out); setSearched(true);
      persistEligibility(med, conds);
      if (out.length === 0) toast.info("No plans on file for your area yet. A licensed insurance agent can help.");
    } catch { toast.error("Couldn't load plans. Please try again."); }
    finally { setLoadingElig(false); }
  };

  const [saveState, setSaveState] = useState("idle");
  const lastSave = useRef(null);
  const tracked = (fn) => {
    lastSave.current = fn;
    setSaveState("saving");
    return fn().then(() => setSaveState("saved")).catch((e) => { setSaveState("failed"); throw e; });
  };
  const retrySave = () => { if (lastSave.current) tracked(lastSave.current).catch(() => {}); };
  const persistGuestCands = (list) => {
    if (!isAgent && !isMember && token) api.post(`/public/ask/${token}/candidates`, { candidates: list.map((c) => ({ ...c, plan_year: 2027 })) }).catch(() => {});
  };
  const toggleCandidate = (c) => {
    const on = candidates.some((x) => keyOf(x) === keyOf(c));
    const next = on ? candidates.filter((x) => keyOf(x) !== keyOf(c)) : [...candidates, c];
    setCandidates(next);
    persistGuestCands(next);
    if (isMember && token) {
      const req = () => (on ? api.delete(`/consumer/ask/${token}/candidates/${c.display_id || keyOf(c)}`)
        : api.post(`/consumer/ask/${token}/candidates`, { ...idOf(c), plan_year: 2027 }));
      tracked(req).catch(() => toast.error("We couldn't save that plan selection. Please try again."));
    }
  };
  const saveMemberCurrent = async (c, prev) => {
    try { await tracked(() => api.post(`/consumer/ask/${token}/current-plan`, { ...idOf(c), plan_year: c.plan_year || 2027 })); }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || "We couldn't save that as your current plan.");
      setCurrent(prev);
    }
  };
  const markCurrent = (c) => {
    const next = current && keyOf(current) === keyOf(c) ? null : c;
    const prev = current;
    setCurrent(next);
    if (!isAgent && !isMember && token) api.post(`/public/ask/${token}/current-plan`, { current: next ? { ...next, plan_year: 2027 } : null }).catch(() => {});
    if (isMember && token && next) saveMemberCurrent(next, prev);
  };

  const runCompare = async () => {
    const cands = candidates.filter((c) => !current || keyOf(c) !== keyOf(current));
    if (cands.length === 0) { toast.error("Add at least one plan to compare (that isn't your current plan)."); return; }
    setComparing(true); setResult(null);
    try {
      let data;
      if (isAgent) {
        const body = { candidates: cands.map(idOf), current: current ? idOf(current) : null, year_candidate: 2027, county, state };
        ({ data } = await api.post("/agent/compare/plans", body));
      } else if (isMember) {
        ({ data } = await api.post(`/consumer/ask/${token}/compare-year`, { candidates: cands.map(idOf), current: current ? idOf(current) : null, year_candidate: 2027 }));
      } else {
        ({ data } = await api.post(`/public/ask/${token}/compare`, { candidates: cands, current, year_candidate: 2027 }));
      }
      setResult({ data, cands, current, labels: Object.fromEntries([...cands, ...(current ? [current] : [])].map((c) => [keyOf(c), c.plan_name || c.display_id])) });
    } catch (e) { toast.error(e?.response?.status === 429 ? "Too many comparisons in a short time. Please wait a minute and try again." : "Comparison failed. Please try again."); }
    finally { setComparing(false); }
  };

  const idOf = (c) => ({ contract_id: c.contract_id, pbp: c.pbp, segment_id: c.segment_id });
  const isSelected = (c) => candidates.some((x) => keyOf(x) === keyOf(c));
  const isCurrent = (c) => current && keyOf(current) === keyOf(c);

  // Promote to the "known" flow if a current plan arrives after mount (e.g. parent prefills it
  // while the dialog is already open) so the current-plan picker + 2027 search render. Skipped in
  // eligibility-prefill mode, which intentionally stays on the guided path.
  useEffect(() => {
    if (initialCurrent && !eligibilityPrefill) setFlow((f) => f || "known");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCurrent]);

  // Guest/returning-member resume: if carried-over plans + a known county are pre-loaded, reopen the
  // comparison exactly where they left off (run it once automatically).
  const didAutoCompare = useRef(false);
  useEffect(() => {
    if (!didAutoCompare.current && (initialCandidates || []).length > 0 && county && !result) {
      didAutoCompare.current = true;
      runCompare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-tap eligibility-filtered compare: when the parent prefills the member's known Medicaid +
  // conditions, auto-load the 2027 plans they qualify for as soon as a county is known.
  const didAutoElig = useRef(false);
  useEffect(() => {
    if (eligibilityPrefill?.autoLoad && !didAutoElig.current && county && medicaid !== "not_sure") {
      didAutoElig.current = true;
      loadEligible(eligibilityPrefill.medicaid, eligibilityPrefill.conditions || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [county]);

  return (
    <div className="space-y-5 min-w-0 max-w-full" data-testid="compare-panel">
      {/* Step 1 — Location */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Where do you live?</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP code" className="h-12 w-32 bg-white border-[#D7D4CB] rounded-xl" data-testid="compare-zip" />
          <Input value={state} onChange={(e) => setState(e.target.value.toUpperCase())} placeholder="ST" maxLength={2} className="h-12 w-16 bg-white border-[#D7D4CB] rounded-xl uppercase" data-testid="compare-state" />
          <Button type="button" onClick={resolveZip} disabled={resolvingZip || !zip.trim()} className="h-12 bg-[#1E3E62] text-white hover:bg-[#0B192C] rounded-xl" data-testid="compare-resolve-zip">
            {resolvingZip ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find county"}
          </Button>
        </div>
        {countyOptions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="compare-county-options">
            {countyOptions.map((c) => (
              <button key={c.county} type="button" onClick={() => { setCounty(c.county); setCountyOptions([]); }}
                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#D7D4CB] bg-white hover:border-[#1E3E62]" data-testid={`compare-county-opt-${c.county}`}>{c.county} County</button>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          <Input value={county} onChange={(e) => setCounty(e.target.value)} placeholder="County (e.g. Orange)" className="h-12 bg-white border-[#D7D4CB] rounded-xl max-w-xs" data-testid="compare-county" />
          {county && <span className="text-[11px] text-[#5B6670] flex items-center gap-1"><Check className="w-3 h-3 text-[#1E3E62]" /> {county} County, {state}</span>}
        </div>
      </div>

      {/* Guided eligibility-first stepper (consumer) — the default, senior-friendly path */}
      {isConsumer && flow === "guided" && county && (
        <div className="space-y-4" data-testid="guided-flow">
          <div className="flex items-center justify-between gap-2">
            <button type="button" onClick={() => { setMedicaid(""); setContinueNotSure(false); setConditions([]); setCards([]); setSearched(false); }}
              className="text-[11px] text-[#5B6670] underline" data-testid="guided-back">← Start over</button>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62]">Do you have Florida Medicaid?</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[["yes", "Yes"], ["no", "No"], ["not_sure", "I'm not sure"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => chooseMedicaid(v)} data-testid={`guided-medicaid-${v}`}
                  className={`text-base rounded-full px-5 py-2.5 font-semibold border transition-colors ${medicaid === v ? "bg-[#1E3E62] text-white border-[#1E3E62]" : "bg-white text-[#0B192C] border-[#D7D4CB] hover:border-[#1E3E62]"}`}>{l}</button>
              ))}
            </div>
          </div>
          {medicaid === "yes" && (
            <p className="text-[11px] text-[#5B6670]" data-testid="guided-dsnp-note">Showing Dual-Eligible (D-SNP) plans built for members with Medicaid.</p>
          )}
          {medicaid === "not_sure" && (
            <div className="rounded-xl border border-[#D4AF37]/50 bg-[#FBF7EC] p-3" data-testid="guided-notsure-handoff">
              <p className="text-sm text-[#0B192C] font-semibold">Not sure about Medicaid?</p>
              <p className="text-[12px] text-[#5B6670] mt-0.5">A licensed insurance agent can check your Medicaid status for you — it affects which plans you qualify for. You can also keep going and we'll show plans that don't require Medicaid.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {onRequestAdvisor && (
                  <Button type="button" onClick={onRequestAdvisor} className="h-10 bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full font-semibold" data-testid="guided-advisor-btn">
                    <HeartHandshake className="w-4 h-4 mr-1.5" /> Talk to a licensed insurance agent
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setContinueNotSure(true)} className="h-10 rounded-full border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C]/10" data-testid="guided-continue-anyway">Continue without checking</Button>
              </div>
            </div>
          )}
          {(medicaid === "no" || (medicaid === "not_sure" && continueNotSure)) && (
            <div data-testid="guided-conditions">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62]">Do you have any of these ongoing conditions?</p>
              <p className="text-[11px] text-[#5B6670] mt-0.5">Optional — this adds special plans built for these conditions. Select any that apply, or skip.</p>
              <div className="mt-2 grid sm:grid-cols-3 gap-2">
                {CONDITIONS.map((c) => (
                  <button key={c.key} type="button" onClick={() => toggleCondition(c.key)} data-testid={`guided-condition-${c.key}`}
                    className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${conditions.includes(c.key) ? "border-[#1E3E62] bg-[#1E3E62]/5" : "border-[#D7D4CB] bg-white hover:border-[#1E3E62]"}`}>
                    <span className="inline-flex items-center gap-1.5 text-base font-semibold text-[#0B192C]">{conditions.includes(c.key) && <Check className="w-3.5 h-3.5 text-[#1E3E62]" />}{c.label}</span>
                  </button>
                ))}
              </div>
              <Button type="button" onClick={() => loadEligible(medicaid, conditions)} disabled={loadingElig} className="mt-3 h-12 bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-xl font-semibold" data-testid="guided-show-plans">
                {loadingElig ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ArrowRight className="w-4 h-4 mr-1.5" />} Show my plans
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2 — Carrier search (advisor always; consumer only in "know your plan" mode) */}
      {(isAgent || flow === "known") && (
      <div>
        {isConsumer && (
          <button type="button" onClick={() => { setFlow("guided"); setCards([]); setSearched(false); }} className="mb-2 text-[11px] text-[#1E3E62] font-semibold underline" data-testid="flow-switch-guided">← See plans I qualify for instead</button>
        )}
        <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62] flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Find plans by carrier</p>
        {isConsumer && (
          <p className="text-[11px] text-[#5B6670] mt-1" data-testid="known-help">{isMember ? "Search a carrier and tap Compare to line plans up next to your current coverage. Search another carrier to add more, then compare." : "Search one carrier, tap This is my current plan on yours and Compare on any others. Search more carriers to add plans, then compare them side by side."}</p>
        )}
        <form onSubmit={(e) => { e.preventDefault(); search(); }} className="mt-2 flex flex-wrap gap-2">
          <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier (e.g. Humana, Aetna, UnitedHealthcare)" className="h-12 bg-white border-[#D7D4CB] rounded-xl flex-1 min-w-[220px]" data-testid="compare-carrier" />
          <Button type="submit" disabled={searching || !carrier.trim() || !county.trim()} className="h-12 bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-xl" data-testid="compare-search">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search plans"}
          </Button>
        </form>
      </div>
      )}

      {/* Step 2b (member) — identify the CURRENT plan against the 2026 catalog (candidates stay 2027) */}
      {isMember && (flow === "known" || searched) && (
        <div data-testid="compare-current-picker">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62] flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Your current plan (2026)</p>
          {current ? (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-[#5B6670]"><Check className="w-3.5 h-3.5 text-[#1E3E62]" /> Current: <span className="font-semibold text-[#0B192C]">{current.plan_name || current.display_id}</span>
              <button type="button" onClick={() => setCurrent(null)} className="underline" data-testid="compare-current-clear">Change</button></div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); searchCurrent(); }} className="mt-2 flex flex-wrap gap-2">
              <Input value={curCarrier} onChange={(e) => setCurCarrier(e.target.value)} placeholder="Your current carrier (2026)" className="h-12 bg-white border-[#D7D4CB] rounded-xl flex-1 min-w-[220px]" data-testid="compare-current-carrier" />
              <Button type="submit" disabled={curSearching || !curCarrier.trim() || !county.trim()} className="h-12 bg-[#1E3E62] text-white hover:bg-[#0B192C] rounded-xl" data-testid="compare-current-search">
                {curSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find my plan"}
              </Button>
            </form>
          )}
          {curSearched && !current && (
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="compare-current-results">
              {curCards.length === 0 ? (
                <p className="text-sm text-[#5B6670]">No 2026 plans on file for {curCarrier} in {county} County.</p>
              ) : curCards.map((c) => (
                <button key={keyOf(c)} type="button" onClick={() => { const prev = current; setCurrent(c); setCurSearched(false); setCurCards([]); if (isMember && token) saveMemberCurrent({ ...c, plan_year: c.plan_year || 2026 }, prev); }}
                  className="text-left rounded-xl border border-[#E7E4DB] bg-white px-3 py-2.5 hover:border-[#D4AF37]" data-testid={`compare-current-pick-${keyOf(c)}`}>
                  <p className="text-sm font-semibold text-[#0B192C]">{c.plan_name}</p>
                  <p className="text-[11px] text-[#8A8F98]">{[c.organization, c.plan_type, c.snp_type].filter(Boolean).join(" · ")} · {c.display_id}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {searched && (
        <div data-testid="compare-results">
          {cards.length === 0 ? (
            <p className="text-sm text-[#5B6670]">No plans on file for {carrier} in {county} County. Try another carrier.</p>
          ) : (
            <>
            {isConsumer && medicaid !== "yes" && conditions.length > 0 && (
              <p className="text-xs text-[#5B6670] mb-2" data-testid="compare-cond-note">
                {expandedGeneral
                  ? `Plans built for your condition(s) first, plus all other plans in ${county} County.`
                  : `Showing ${cards.length} plan${cards.length === 1 ? "" : "s"} built for your condition(s) — the plans you qualify for based on your answers.`}
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
              {cards.map((c) => (
                <div key={keyOf(c)} className={`rounded-xl border px-3 py-2.5 bg-white ${isSelected(c) || isCurrent(c) ? "border-[#1E3E62]" : "border-[#E7E4DB]"}`} data-testid={`compare-card-${keyOf(c)}`}>
                  <p className="text-sm font-semibold text-[#0B192C]">{c.plan_name}</p>
                  <p className="text-[11px] text-[#8A8F98]">{[c.organization, c.plan_type, c.snp_type].filter(Boolean).join(" · ")} · {c.display_id}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => toggleCandidate(c)} data-testid={`compare-add-${keyOf(c)}`}
                      className={`text-sm rounded-full px-3.5 py-2 font-semibold inline-flex items-center gap-1 ${isSelected(c) ? "bg-[#1E3E62] text-white" : "bg-[#0B192C]/5 text-[#0B192C] hover:bg-[#0B192C]/10"}`}>
                      {isSelected(c) ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Compare</>}
                    </button>
                    {!isMember && (
                    <button type="button" onClick={() => markCurrent(c)} data-testid={`compare-current-${keyOf(c)}`}
                      className={`text-sm rounded-full px-3.5 py-2 font-semibold inline-flex items-center gap-1 ${isCurrent(c) ? "bg-[#D4AF37] text-[#0B192C]" : "bg-[#0B192C]/5 text-[#0B192C] hover:bg-[#0B192C]/10"}`}>
                      {isCurrent(c) ? <><Check className="w-3 h-3" /> My current plan</> : "This is my current plan"}
                    </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {isConsumer && medicaid !== "yes" && conditions.length > 0 && !expandedGeneral && (
              <button type="button" onClick={() => loadEligible(medicaid, conditions, true)} disabled={loadingElig}
                className="mt-3 text-sm text-[#1E3E62] font-semibold underline underline-offset-2 inline-flex items-center gap-1 disabled:opacity-60" data-testid="compare-view-all">
                {loadingElig ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} View all other plans in {county} County
              </button>
            )}
            </>
          )}
        </div>
      )}

      {/* Selected summary + compare */}
      {(candidates.length > 0 || current) && (
        <div className="rounded-xl border border-[#E7E4DB] bg-[#F7F6F2] p-3 min-w-0" data-testid="compare-selection">
          <div className="flex flex-wrap gap-1.5 min-w-0">
            {current && <span className="text-[11px] bg-[#D4AF37] text-[#0B192C] rounded-full px-2.5 py-1 font-semibold inline-flex items-center gap-1 max-w-full min-w-0"><span className="truncate">Current: {current.plan_name}</span><button type="button" onClick={() => setCurrent(null)} data-testid="compare-remove-current"><X className="w-3 h-3" /></button></span>}
            {candidates.filter((c) => !current || keyOf(c) !== keyOf(current)).map((c) => (
              <span key={keyOf(c)} className="text-[11px] bg-white border border-[#D7D4CB] text-[#0B192C] rounded-full px-2.5 py-1 inline-flex items-center gap-1 max-w-full min-w-0" data-testid={`compare-chip-${keyOf(c)}`}><span className="truncate">{c.plan_name}</span><button type="button" onClick={() => toggleCandidate(c)} data-testid={`compare-remove-${keyOf(c)}`}><X className="w-3 h-3" /></button></span>
            ))}
          </div>
          <Button type="button" onClick={runCompare} disabled={comparing} className="mt-3 h-12 bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-xl font-semibold" data-testid="compare-run">
            {comparing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ArrowRight className="w-4 h-4 mr-1.5" />} Compare 2027 plan facts
          </Button>
        </div>
      )}

      {/* Results */}
      {result && <ComparisonResult result={result} keyOf={keyOf} onSaveInvite={onSaveInvite} isAgent={isAgent} isMember={isMember} onRequestAdvisor={onRequestAdvisor}
        onSelfEnroll={onSelfEnroll} onSaveCompareAdvisor={onSaveCompareAdvisor}
        onShare={onShare ? () => onShare({ candidates: result.cands.map(idOf), current: result.current ? idOf(result.current) : null, county, state, year_candidate: 2027 }) : null} />}
      {(result || cards.length > 0) && !isAgent && token && <MyDoctors plans={(cards.length > 0 ? cards : result.cands).slice(0, 60).map(idOf)} zip={zip} token={token} isMember={isMember} onRequestAgent={onRequestAdvisor} />}
      {(result || cards.length > 0) && !isAgent && token && <MyMedications plans={(cards.length > 0 ? cards : result.cands).slice(0, 60).map(idOf)} token={token} isMember={isMember} onRequestAgent={onRequestAdvisor} />}
      {(result || cards.length > 0) && !isAgent && token && (isMember || onSaveInvite) && <SaveHouseholdBar isMember={isMember} saveState={saveState} onRetry={retrySave} onSaveInvite={onSaveInvite} />}
    </div>
  );
}

function SaveHouseholdBar({ isMember, saveState, onRetry, onSaveInvite }) {
  const [kb, setKb] = useState(false);
  useEffect(() => {
    const on = (e) => { if (e.target.matches?.("input, textarea, select")) setKb(true); };
    const off = () => setKb(false);
    document.addEventListener("focusin", on); document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); };
  }, []);
  const go = () => {
    if (isMember) return window.location.assign("/app/member#my-household");
    try { sessionStorage.setItem("ask_after_save", "household"); } catch { /* ignore */ }
    return onSaveInvite();
  };
  const label = isMember ? "View in My Nauticus" : "Save to My Nauticus";
  const status = isMember && saveState !== "idle" && (
    <span className="text-xs" data-testid="save-state" aria-live="polite">
      {saveState === "saving" && <span className="text-[#5B6670]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Saving…</span>}
      {saveState === "saved" && <span className="text-[#166534]"><Check className="w-3 h-3 inline mr-1" />Saved</span>}
      {saveState === "failed" && <span className="text-[#B42318]">Couldn't save. <button type="button" onClick={onRetry} className="underline font-semibold" data-testid="save-retry">Retry</button></span>}
    </span>
  );
  return (
    <>
      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="save-household-inline">
        <Button type="button" onClick={go} className="bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] rounded-full h-12 text-base font-semibold" data-testid="save-household-btn">
          <CheckCircle2 className="w-4 h-4 mr-1.5" />{label}
        </Button>
        {status}
        {!isMember && <span className="text-xs text-[#5B6670]">Saving doesn't sign you up for agent contact.</span>}
      </div>
      {!kb && (
        <div className="sm:hidden sticky bottom-0 -mx-4 mt-3 border-t border-[#E7E4DB] bg-white/95 backdrop-blur px-4 py-2 flex items-center gap-2" data-testid="save-household-sticky">
          <Button type="button" onClick={go} className="flex-1 bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] rounded-full h-11 font-semibold" data-testid="save-household-sticky-btn">{label}</Button>
          {status}
        </div>
      )}
    </>
  );
}

function ComparisonResult({ result, keyOf, onSaveInvite, isAgent, isMember, onShare, onSelfEnroll, onSaveCompareAdvisor, onRequestAdvisor }) {
  const { data, labels } = result;
  if (!data?.ok) return <p className="text-sm text-[#5B6670]" data-testid="compare-empty">Nothing to compare yet.</p>;
  const rows = (data.rows || []).filter((r) => (r.current && r.current.text !== "not available") || r.candidates.some((c) => c.text !== "not available"));
  const unknown = data.limits?.fields_with_unknown_values || [];
  const ineligible = data.county_eligibility?.ineligible_candidates || [];
  return (
    <div className="rounded-xl border border-[#E7E4DB] bg-white p-4 min-w-0" data-testid="compare-output">
      <p className="text-sm font-semibold text-[#0B192C] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#1E3E62]" /> Supported 2027 plan facts{data.current ? " vs your current plan" : ""}</p>

      {(data.candidates || []).some((b) => (b.highlights || []).length > 0) && (
        <div className="mt-3 space-y-2" data-testid="compare-highlights">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8F98]">{data.current ? "What changes vs your current plan" : "Standout 2027 benefits"}</p>
          {(data.candidates || []).map((b) => ((b.highlights || []).length > 0 && (
            <div key={b.display_id} className="rounded-xl bg-[#F7F6F2] border border-[#E7E4DB] p-2.5" data-testid={`highlights-${b.display_id}`}>
              <p className="text-[11px] font-semibold text-[#0B192C] mb-1.5">{(labels[b.display_id] || b.plan_name || b.display_id).slice(0, 36)}</p>
              <div className="flex flex-wrap gap-1.5">
                {b.highlights.map((h) => (
                  <span key={h.key} data-testid={`highlight-${b.display_id}-${h.key}`}
                    className={`text-xs rounded-full px-3 py-1 font-semibold inline-flex items-center gap-1.5 border ${h.tone === "positive" ? "bg-[#ECFDF3] text-[#067647] border-[#ABEFC6]" : h.tone === "negative" ? "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" : "bg-white text-[#0B192C] border-[#D7D4CB]"}`}>
                    <span className="opacity-60 font-medium">{h.label}</span>
                    <span>{h.delta_text || h.value_text}</span>
                  </span>
                ))}
              </div>
            </div>
          )))}
        </div>
      )}

      <details className="mt-3 rounded-lg border border-[#E7E4DB]" data-testid="compare-details">
        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-[#1E3E62]" data-testid="compare-details-toggle">All benefit details ({rows.length})</summary>
      <div className="overflow-x-auto px-2 pb-2">
        <table className="w-full text-[11px] border-collapse" data-testid="compare-table">
          <thead>
            <tr className="text-left text-[#8A8F98]">
              <th className="py-1.5 pr-3 font-medium">Benefit</th>
              {data.current && <th className="py-1.5 pr-3 font-medium">{(data.current.plan_name || "Current").slice(0, 22)}</th>}
              {(data.candidates || []).map((b) => (
                <th key={b.display_id} className="py-1.5 pr-3 font-medium">{(labels[b.display_id] || b.plan_name || b.display_id).slice(0, 22)}{ineligible.includes(b.display_id) ? " ⚠" : ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-[#E7E4DB]" data-testid={`compare-benefit-${r.key}`} data-benefit={r.key}>
                <td className="py-1.5 pr-3 text-[#5B6670]">{r.label}</td>
                {data.current && <td className="py-1.5 pr-3 text-[#0B192C]">{r.current?.text || "not available"}</td>}
                {r.candidates.map((c) => (
                  <td key={c.display_id} className="py-1.5 pr-3 text-[#1E3E62]">{c.text}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </details>
      {ineligible.length > 0 && (
        <p className="mt-2 text-[11px] text-[#B45309]" data-testid="compare-ineligible">⚠ Marked plans don't serve the county you entered and aren't valid options there.</p>
      )}
      {unknown.length > 0 && (
        <p className="mt-2 text-[11px] text-[#5B6670]" data-testid="compare-unknown">Not yet verified for 2027: {unknown.slice(0, 5).join(", ")}{unknown.length > 5 ? "…" : ""}. Shown as not verified rather than guessed.</p>
      )}
      <p className="mt-2 text-[11px] text-[#8A8F98]" data-testid="compare-limits">
        Doctor and drug-list checks are in "Your doctors" and "Your medications" below. This is factual plan data, not a ranking or recommendation. Drug costs aren't estimated.
      </p>
      {!isAgent && onRequestAdvisor && <ReviewCard year={data?.year_candidate || 2027} onRequest={() => onRequestAdvisor("Doctor and prescription review")} />}
      {isMember && (onSelfEnroll || onSaveCompareAdvisor) && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="member-compare-actions">
          {onSaveCompareAdvisor && (
            <Button type="button" onClick={() => onSaveCompareAdvisor(result.cands, result.current)} className="bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full h-12 text-base font-semibold" data-testid="member-save-advisor-btn">
              <HeartHandshake className="w-4 h-4 mr-1.5" /> Save &amp; request an agent
            </Button>
          )}
        </div>
      )}
      {isMember && onSelfEnroll && (
        <div className="mt-3">
          <button type="button" onClick={() => onSelfEnroll(result.cands)} className="text-sm text-[#1E3E62] font-semibold underline underline-offset-2" data-testid="member-self-enroll-btn">Explore plans on Sunfire</button>
          <p className="mt-1 text-[11px] text-[#5B6670]" data-testid="member-self-enroll-note">Sunfire opens separately in a new window. Your information from Nauticus doesn't carry over, so you'll need to enter it again there. Opening Sunfire isn't a submitted application.</p>
        </div>
      )}
      {!isAgent && !isMember && onSaveInvite && (
        <Button type="button" onClick={onSaveInvite} className="mt-3 bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] rounded-full h-12 text-base font-semibold" data-testid="compare-save-invite">
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Save these results & keep going
        </Button>
      )}
      {isAgent && onShare && (
        <Button type="button" onClick={onShare} className="mt-3 bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full h-12 text-base font-semibold" data-testid="compare-share-btn">
          <Mail className="w-4 h-4 mr-1.5" /> Email this comparison to the member
        </Button>
      )}
    </div>
  );
}
